import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useState, type ReactNode } from 'react';

import { ErrorState } from '@/components/common/ErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Layout } from '@/components/Layout';
import { NotInvitedScreen } from '@/components/NotInvitedScreen';
import { SignedOutLanding } from '@/components/SignedOutLanding';
import { useLogout } from '@/hooks/useLogout';
import {
  ALLOWLIST_DENIAL_REASON,
  AUTH0_ACCESS_DENIED_CODE,
} from '@/lib/auth0Constants';

interface RequireAuthProps {
  children: ReactNode;
}

// `@auth0/auth0-react` parses `error=…&error_description=…` from the
// redirect callback into an OAuthError that extends Error and carries
// `.error` (the OAuth code) and `.error_description` as own properties.
// The SDK derives `.message` from `error_description` — but if the
// Action ever calls `api.access.deny(reason, userMessage)`, the
// userMessage overrides error_description and the message-based match
// silently breaks. So we check both the OAuth code and the
// description, and fall back to .message for safety.
interface OAuthErrorLike {
  error?: unknown;
  error_description?: unknown;
}

function isNotAllowlistedError(error: Error): boolean {
  const shaped = error as Error & OAuthErrorLike;
  const code = typeof shaped.error === 'string' ? shaped.error : undefined;
  const description =
    typeof shaped.error_description === 'string'
      ? shaped.error_description
      : undefined;
  if (code === AUTH0_ACCESS_DENIED_CODE && description === ALLOWLIST_DENIAL_REASON) {
    return true;
  }
  // Older SDK shapes / non-OAuthError surfaces only expose .message.
  return error.message === ALLOWLIST_DENIAL_REASON;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    error: auth0Error,
  } = useAuth0();
  const signOutAndRestart = useLogout();
  const [redirectError, setRedirectError] = useState<Error | null>(null);

  const startLogin = useCallback(() => {
    setRedirectError(null);
    // Preserve the requested URL across the Universal Login round-trip so
    // deep links (e.g. /coffee/<id> opened from email or a shared link)
    // land on the intended page after sign-in. The Auth0Provider's
    // onRedirectCallback (main.tsx) consumes appState.returnTo.
    const returnTo =
      window.location.pathname + window.location.search + window.location.hash;
    loginWithRedirect({ appState: { returnTo } }).catch((err: unknown) => {
      console.error('Auth0 loginWithRedirect failed', err);
      setRedirectError(err instanceof Error ? err : new Error(String(err)));
    });
  }, [loginWithRedirect]);

  // `signOutAndRestart` (useLogout) clears the SSO cookie — the only
  // reliable escape from a server-side deny rule (allowlist, MFA, etc.).
  // `loginWithRedirect` would just silently re-authenticate via SSO and
  // trigger the same denial; useLogout's `returnTo: origin` drops the
  // user on a bare URL so the next mount has no error param.

  // Gate this on `!isAuthenticated`: `useAuth0().error` is not only
  // populated by the redirect callback — the auth0-react reducer also
  // sets it when post-auth SDK calls like `getAccessTokenSilently`
  // fail (used by useAuthedClient on every request). Without the gate,
  // a transient token-refresh failure would kick an allowlisted, signed-in
  // user to the error screen (or, worst case, to NotInvitedScreen if
  // the failure shape happens to match).
  if (!isAuthenticated && auth0Error) {
    if (isNotAllowlistedError(auth0Error)) {
      return <NotInvitedScreen />;
    }
    // Other Auth0 callback errors (consent_required, invalid_grant,
    // non-allowlist `access_denied` reasons, etc.) — retrying via
    // loginWithRedirect would re-issue against the same SSO cookie and
    // hit the same server-side rule. Force a sign-out so the next
    // attempt starts from a clean session.
    return (
      <Layout>
        <ErrorState
          heading="Sign-in failed"
          message="We couldn't sign you in. Please try again."
          onPrimaryAction={() => void signOutAndRestart()}
          primaryActionLabel="Sign out and try again"
          testId="require-auth-error"
        />
      </Layout>
    );
  }

  if (isLoading) {
    return <LoadingSpinner label="Signing you in" testId="require-auth-loading" />;
  }
  if (!isAuthenticated) {
    if (redirectError) {
      // Without this branch, third-party-cookie blockers, Auth0 outages,
      // CSP misconfigs, or transient network errors leave the user on a
      // forever spinner with no diagnostic. Unlike the auth0Error path
      // above, this one is a client-side failure to *start* the redirect
      // — Universal Login never ran, so retrying it is safe.
      return (
        <Layout>
          <ErrorState
            heading="Sign-in failed"
            message="We couldn't sign you in. Please try again."
            onPrimaryAction={startLogin}
            primaryActionLabel="Try again"
            testId="require-auth-error"
          />
        </Layout>
      );
    }
    return <SignedOutLanding onLogin={startLogin} />;
  }
  return <>{children}</>;
}
